import React from "react";
import { formatDecimal, formatMoney, formatDateTime, formatText, formatDate } from "../../utils/formatters";
// (2026-07-22 Viajes) Fuente única de los 15 campos del nodo `/viajes`.
import { VIAJE_CAMPOS } from "../../utils/viajeCampos";

// =============================================================================
// (2026-08-02, ítem 32) Helpers de LÍNEA compartidos por las vistas de detalle.
// Se exportan desde aquí y ModalProformas los IMPORTA: las dos superficies
// mostraban el mismo dato con dos fórmulas distintas (la modal de proforma ni
// siquiera resolvía las columnas `_aplicada`), y esa duplicación es justo lo que
// dejó pasar el bug del total de línea. Funciones PURAS y sin JSX: la suite
// src/utils/__tests__/proformas_calculos.test.js las extrae VERBATIM de este
// archivo, así que un cambio aquí se prueba con el cambio.
// =============================================================================

// Resolución del par C2/C1: `<campo>_aplicada` (monto en la moneda en que se
// emitió el documento) con fallback a la columna plana (VES / legacy).
// Mismo patrón que el bloque de totales (`total_base_aplicada ?? total_base`).
export const valorAplicado = (item, campo) => {
  if (!item) return 0;
  const ap = item[campo + "_aplicada"];
  if (ap !== undefined && ap !== null && ap !== "") return Number(ap) || 0;
  const plano = item[campo];
  if (plano === undefined || plano === null || plano === "") return 0;
  return Number(plano) || 0;
};

// Precio unitario a mostrar: 1) precio_unitario_aplicada (multi-moneda),
// 2) precio_unitario (FC) / monto_unitario (NC-ND legacy).
export const precioUnitarioAplicado = (item, tipoDocumento) => {
  if (!item) return 0;
  if (item.precio_unitario_aplicada !== undefined && item.precio_unitario_aplicada !== null) {
    return Number(item.precio_unitario_aplicada) || 0;
  }
  // NC/ND de FACTURA traen `monto_unitario`; los ítems de PREFACTURA (incluidos
  // los borradores de NC/ND) siempre traen `precio_unitario` y NO tienen
  // `monto_unitario` — de ahí el fallback: sin él, la modal de proforma de una
  // NC mostraría 0,00 en todas las líneas.
  let plano = tipoDocumento === "FC" ? item.precio_unitario : item.monto_unitario;
  if (plano === undefined || plano === null || plano === "") plano = item.precio_unitario;
  return Number(plano) || 0;
};

// Descuento efectivo de la línea — ESPEJO EXACTO de utils.py:563-575:
// el PORCENTAJE tiene PRECEDENCIA cuando NO es NULL; solo si es NULL se usa
// `descuento_monto`. Un 0 explícito en el porcentaje desactiva el monto (esa
// semántica es load-bearing: por eso `descuento_porcentaje` está excluido de la
// coerción NOT NULL del backend, servicios_compartidos.py:47-55).
export const descuentoDeLinea = (item, subtotal) => {
  if (!item) return 0;
  const pct = item.descuento_porcentaje;
  if (pct !== undefined && pct !== null && pct !== "") {
    return (Number(subtotal) || 0) * ((Number(pct) || 0) / 100);
  }
  return valorAplicado(item, "descuento_monto");
};

// Total de la línea SIN IVA — espejo del backend:
//   precioItem = cantidad * precio - descuento(% Ó monto) + recargo
// (utils.py:561-590; el recargo de línea se SUMA a la base y NO se acota).
export const totalLineaCalculado = (item, tipoDocumento) => {
  if (!item) return 0;
  const cantidad = Number(item.cantidad) || 0;
  const subtotal = cantidad * precioUnitarioAplicado(item, tipoDocumento);
  const total = subtotal - descuentoDeLinea(item, subtotal) + valorAplicado(item, "recargo_monto");
  return Math.round(total * 100) / 100;
};

// Total de línea A MOSTRAR.
//
// ⚠️ HALLAZGO (2026-08-02, backend — NO corregible desde la capa web): la
// conversión proforma → factura calcula las columnas persistidas
// `total`/`total_aplicada`/`total_extranjero` como `base - base*desc%`
// (factura_routes.py:2299-2305): IGNORA `descuento_monto` y `recargo_monto`.
// Por eso NO se puede confiar en el valor almacenado cuando la línea trae uno de
// esos dos importes: se recalcula. Para el resto de las líneas se conserva el
// valor del backend, así que ningún documento que hoy se ve bien cambia.
export const totalLineaMostrado = (item, tipoDocumento) => {
  if (!item) return 0;
  const tieneMontoOReCargo =
    valorAplicado(item, "descuento_monto") > 0 || valorAplicado(item, "recargo_monto") > 0;
  if (!tieneMontoOReCargo && item.total_aplicada !== undefined && item.total_aplicada !== null) {
    return Number(item.total_aplicada) || 0;
  }
  return totalLineaCalculado(item, tipoDocumento);
};

const InvoiceDetailView = ({ message }) => {
  const hasNotaDebitoItems =
    message?.tipo_documento === "ND" &&
    message?.nota_debito_detalle?.items?.length > 0 &&
    !message?.nota_debito_detalle?.items[0].producto;

  let itemsList;
  if (message.tipo_documento === 'ND' && message.nota_debito_detalle?.items?.length > 0) {
    itemsList = message.nota_debito_detalle.items;
  } else if (message.tipo_documento === 'NC' && message.nota_credito_detalle?.items?.length > 0) {
    itemsList = message.nota_credito_detalle.items;
  } else {
    itemsList = message.items;
  }

  return (
    <>
      {/* Información general */}
      <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2 px-2">
        Información del Documento
      </h6>
      <div className="flex flex-wrap justify-between px-2 mb-4">
        <div className="w-1/5 text-start">
          <label className="font-bold text-blueGray-700">Fecha de {(message.tipo_documento == 'FC' ? 'Factura' : (message.tipo_documento == 'ND' ? 'Nota de Débito' : 'Nota de Crédito') )}:</label>
          <div>{message.fecha_factura ? formatDate(message.fecha_factura):''}</div>
        </div>
        <div className="w-1/5 text-start">
          <label className="font-bold text-blueGray-700">Número de control:</label>
          <div>{message.numero_control}</div>
        </div>
        <div className="w-1/5 text-start">
          <label className="font-bold text-blueGray-700">Número de documento:</label>
          <div>{(message.numero_documento || message.numero_factura) ? (message.numero_documento || message.numero_factura).toUpperCase() : ''}</div>
        </div>
        <div className="w-1/5 text-start">
          <label className="font-bold text-blueGray-700">Correlativo:</label>
          <div>{message.correlativo_interno}</div>
        </div>
        {/* (#117 Increment 1b) Columna "Serial" RETIRADA: el ex-`serial` es ahora
            el Nº de Control unificado y ya se muestra en "Número de control". */}
      </div>

      <hr className="my-4 border-b border-blueGray-300"/>

      {/* Cliente (comprador) */}
      <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2 px-2">
        Datos del Cliente
      </h6>
      <div className="flex flex-wrap justify-between px-2 mb-4">
        <div className="w-1/5 text-start">
          <label className="font-bold text-blueGray-700">RIF:</label>
          <div>{message.cliente?.rif ?? message.cliente_rif}</div>
        </div>
        <div className="w-2/5 text-start">
          <label className="font-bold text-blueGray-700">Razón Social:</label>
          <div>{message.cliente?.nombre ?? message.cliente_nombre}</div>
        </div>
        <div className="w-2/5 text-start">
          <label className="font-bold text-blueGray-700">Dirección:</label>
          <div>{(() => { const v = message.cliente?.direccion ?? message.cliente_direccion; return v ? v.toUpperCase() : ''; })()}</div>
        </div>
        <div className="w-1/5 text-start">
          <label className="font-bold text-blueGray-700">Teléfono:</label>
          <div>{(message.cliente?.telefono ?? message.cliente_telefono) || ''}</div>
        </div>
      </div>

      <hr className="my-4 border-b border-blueGray-300"/>

      {/* Documento y Estado */}
      <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2 px-2">
        Estado del Documento
      </h6>
      <div className="flex flex-wrap justify-between px-2 mb-4">
        <div className="w-1/3 text-start">
          <label className="font-bold text-blueGray-700">Tipo de documento:</label>
          <div>
            {message.tipo_documento === 'FC'
              ? 'FACTURA'
              : message.tipo_documento === 'NC'
              ? 'NOTA DE CREDITO'
              : 'NOTA DE DEBITO'}
          </div>
        </div>
        <div className="w-1/3 text-start">
          <label className="font-bold text-blueGray-700">Estado:</label>
          <div>{message.estatus.toUpperCase()}</div>
        </div>
        {/* (2026-05-13) Req 2: moneda de la factura */}
        <div className="w-1/3 text-start">
          <label className="font-bold text-blueGray-700">Moneda:</label>
          <div>
            {message.moneda
              ? `${message.moneda.codigo} (${message.moneda.simbolo})`
              : '—'}
            {message.tasa && (
              <span className="text-xs text-blueGray-400 ml-2">
                {/* (2026-08-02, ítem 32-iii) 4 decimales: el PDF oficial y la
                    vista pública ya imprimen la tasa con 4 (formatMoneySpecial
                    /fmt(...,4)). Con 2 esta modal mostraba una tasa REDONDEADA
                    distinta de la que consta en el documento fiscal. */}
                tasa: {formatDecimal(message.tasa, 4)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* (2026-05-13) Req 2: totales en la moneda APLICADA de la factura.
          Las columnas `_aplicada` representan el monto real en la moneda en
          que se emitio la factura (USD/EUR/VES). Las columnas legacy VES
          quedan disponibles a nivel de BD para SENIAT (libro de ventas) pero
          NO se muestran al usuario final aqui — esa info ya va en el PDF
          oficial y en la lista del libro. Las columnas `_extranjero` son
          referencia historica interna; no se exponen en esta vista. */}
      {message.total_neto_aplicada != null && (
        <div className="flex flex-wrap justify-between px-2 mb-4 bg-slate-50 p-3 rounded">
          <div className="w-1/4 text-start">
            <label className="font-bold text-blueGray-700">Base imponible:</label>
            <div>{formatMoney(message.total_base_aplicada, message.moneda)}</div>
          </div>
          <div className="w-1/4 text-start">
            <label className="font-bold text-blueGray-700">Impuestos:</label>
            <div>{formatMoney(message.total_impuestos_aplicada, message.moneda)}</div>
          </div>
          {message.igtf_monto_aplicada != null && Number(message.igtf_monto_aplicada) > 0 && (
            <div className="w-1/4 text-start">
              <label className="font-bold text-blueGray-700">IGTF:</label>
              <div>{formatMoney(message.igtf_monto_aplicada, message.moneda)}</div>
            </div>
          )}
          <div className="w-1/4 text-start">
            <label className="font-bold text-blueGray-700">Total:</label>
            <div className="font-bold">
              {formatMoney(message.total_neto_aplicada, message.moneda)}
            </div>
          </div>
        </div>
      )}

      {/* (2026-05-13) Req 1: bloque de facturación de tercero — solo si aplica */}
      {message.doc_tercero && (
        <div className="px-2 mb-4">
          <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2">
            Datos del Tercero (Afiliada)
          </h6>
          <label className="font-bold text-blueGray-700 mb-2 text-twilight-indigo-700">
            <i className="fas fa-handshake mr-1"></i>
            Documento de Tercero:
          </label>
          <div className="flex flex-wrap justify-between mt-2 bg-twilight-indigo-50 p-3 rounded">
            <div className="w-1/3 text-start">
              <label className="font-bold text-blueGray-700">Tercero:</label>
              <div>
                {message.tercero
                  ? `${formatText(message.tercero.nombre_empresa)} (${message.tercero.rif})`
                  : '—'}
              </div>
            </div>
            <div className="w-1/3 text-start">
              <label className="font-bold text-blueGray-700">Comisión:</label>
              <div>
                {message.comision_servicio != null
                  ? `${formatDecimal(message.comision_servicio)} %`
                  : '—'}
              </div>
            </div>
            <div className="w-1/3 text-start">
              <label className="font-bold text-blueGray-700">Comisión aplicada:</label>
              <div>
                {message.comision_calculada_aplicada != null
                  ? formatMoney(message.comision_calculada_aplicada, message.moneda)
                  : '—'}
              </div>
            </div>
          </div>
          {message.facturas_comision && message.facturas_comision.length > 0 && (
            <div className="mt-2 text-xs text-blueGray-500">
              <i className="fas fa-link mr-1" />
              Factura(s) de comisión vinculada(s):{" "}
              {message.facturas_comision.map(fc => fc.numero_control).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* (2026-07-22 Viajes) Nodo `/viajes` — datos de pasajero y boleto de la
          factura. Render ESTRICTAMENTE condicional: una factura sin boletos se
          ve exactamente igual que antes. Un documento puede cubrir N boletos. */}
      {(message.viajes || []).length > 0 && (
        <div className="px-2 mb-4">
          <h6 className="text-blueGray-400 text-xs font-bold uppercase mb-2">
            Datos del Viaje (Boletería Aérea)
          </h6>
          <label className="font-bold text-blueGray-700 mb-2 text-twilight-indigo-700">
            <i className="fas fa-plane-departure mr-1"></i>
            {message.es_boleteria_aerea
              ? 'Documento de boletería aérea:'
              : 'Datos de viaje asociados:'}
          </label>
          {message.viajes.map((viaje, idx) => (
            <div key={viaje.id || idx} className="flex flex-wrap mt-2 mb-2 bg-twilight-indigo-50 p-3 rounded">
              <div className="w-full font-bold text-blueGray-700 text-xs uppercase mb-2">
                Boleto #{viaje.numero_linea || idx + 1}
              </div>
              {VIAJE_CAMPOS.map((campo) => (
                <div key={campo.key} className="w-1/3 text-start mb-1 pr-3">
                  <label className="font-bold text-blueGray-700">{campo.label}:</label>
                  <div>{formatText(viaje[campo.key] || '') || '—'}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {(message.tipo_documento === 'ND' || message.tipo_documento === 'NC') && message.factura_afectada_rel && (
        <hr className="my-4 border-b border-blueGray-300"/>
      )}

      {(message.tipo_documento === 'ND' || message.tipo_documento === 'NC') && message.factura_afectada_rel && (
        <div className="mb-4 px-2">
          {/* Título principal */}
          <label className="font-bold text-blueGray-700 mb-2">Factura afectada:</label>

          {/* Datos de la factura afectada */}
          <div className="flex flex-wrap justify-between mt-3">
            <div className="w-1/5 text-start">
              <label className="font-bold text-blueGray-700">Número de control:</label>
              <div>{formatText(message.factura_afectada_rel.numero_control)}</div>
            </div>
            <div className="w-1/5 text-center">
              <label className="font-bold text-blueGray-700">Fecha de emisión:</label>
              <div>{formatDateTime(message.factura_afectada_rel.fecha_emision)}</div>
            </div>
            <div className="w-1/5 text-end">
              <label className="font-bold text-blueGray-700">Total:</label>
              <div>{formatMoney(message.factura_afectada_rel.total_neto)}</div>
            </div>
          </div>
          <hr className="my-4 border-b border-blueGray-300"/>
        </div>
      )}

      <hr className="my-4 border-b border-blueGray-300"/>

      {/* Tabla de Items
          (2026-05-13) Req 2: el precio y el total se muestran en la moneda
          aplicada (la moneda en que se emitio la factura). Para NC/ND legacy
          (sin _aplicada poblado) o facturas pre-multimoneda, fallback al
          precio en VES. */}
      {!hasNotaDebitoItems && (
      <div className="overflow-x-auto mb-4">

          {/* (2026-08-02, ítem 32-ii) Columnas "Desc." (descuento por MONTO
              fijo) y "Recargo": ambos importes ya viajaban en el payload y no se
              pintaban, de modo que dos líneas con el mismo precio y distinto
              descuento/recargo se veían idénticas. Anchos rebalanceados a 12/12:
              3+1+1+2+1+1+1+2. */}
          <div className="flex bg-gray-100 border-b-2 border-gray-300 font-bold text-center">
            <div className="w-3/12 px-2 py-1">Producto</div>
            <div className="w-1/12 px-2 py-1">Cantidad</div>
            <div className="w-1/12 px-2 py-1">Impuesto</div>
            <div className="w-2/12 px-2 py-1">
              Precio Unitario{message.moneda?.codigo ? ` (${message.moneda.codigo})` : ''}
            </div>
            <div className="w-1/12 px-2 py-1">Desc %</div>
            <div className="w-1/12 px-2 py-1">Desc.</div>
            <div className="w-1/12 px-2 py-1">Recargo</div>
            <div className="w-2/12 px-2 py-1">
              Total{message.moneda?.codigo ? ` (${message.moneda.codigo})` : ''}
            </div>
          </div>
        {!hasNotaDebitoItems && itemsList && itemsList.length > 0 ? (
          itemsList.map((item) => {
            // Precio unitario, descuento por monto, recargo y total: helpers
            // compartidos (módulo, arriba). El total ya NO ignora el descuento
            // por monto fijo ni el recargo de línea.
            const precioAplicado = precioUnitarioAplicado(item, message.tipo_documento);
            const descuentoMonto = valorAplicado(item, 'descuento_monto');
            const recargoMonto = valorAplicado(item, 'recargo_monto');
            const totalAplicado = totalLineaMostrado(item, message.tipo_documento);
            return (
              <div key={item.id} className="flex border-b border-gray-300 text-center">
                <div className="w-3/12 px-2 py-1 text-left">
                  {item.producto ? item.producto.sku + '-' + item.producto.nombre : 'N/A'}
                </div>
                <div className="w-1/12 px-2 py-1">{formatDecimal(item.cantidad)}</div>
                <div className="w-1/12 px-2 py-1">
                  {item.iva_categoria_id ? formatDecimal(item.iva_categoria.tasa_porcentaje) : '0,00'}
                </div>
                <div className="w-2/12 px-2 py-1 text-right">{formatDecimal(precioAplicado)}</div>
                <div className="w-1/12 px-2 py-1">
                  {item.descuento_porcentaje ? formatDecimal(item.descuento_porcentaje) : '0,00'}
                </div>
                <div className="w-1/12 px-2 py-1 text-right">
                  {descuentoMonto > 0 ? formatDecimal(descuentoMonto) : '—'}
                </div>
                <div className="w-1/12 px-2 py-1 text-right">
                  {recargoMonto > 0 ? formatDecimal(recargoMonto) : '—'}
                </div>
                <div className="w-2/12 px-2 py-1 text-right">{formatDecimal(totalAplicado)}</div>
              </div>
            );
          })
        ) : (
          <div className="flex text-center border-b border-gray-300" >
            <div className="w-full px-2 py-2">No hay líneas asociadas</div>
          </div>
        )}
      </div>
      )}
      {hasNotaDebitoItems && (
        <div className="px-5 flex justify-between border-4 border-double p-2 mb-2">
          <span className="lg:w-8/12 text-center font-bold">Concepto</span>
          <span className="lg:w-4/12 text-center font-bold">Monto</span>
        </div>
      )}
      {hasNotaDebitoItems && (
        itemsList.map(item => (
          <div key={item.id} className="px-5 flex justify-between border-b py-1">
            <span className="lg:w-8/12 text-start">
              {formatText(item.concepto)}
            </span>
            <span className="lg:w-4/12 text-right">
              {formatDecimal(item.monto)}
            </span>
          </div>
        ))
      )}

      <div className="flex flex-wrap">
                  <div className="flex w-full lg:w-8/12 px-2 ml-0 lg:ml-0 mt-0 pb-3 flex-1 min-w-0 border border-gray-200 rounded-lg shadow-sm">
                    <div className="w-full lg:w-9/12 px-1 mt-3 pl-0">
                      <div className="relative w-full mb-3 text-end">
                        <div className="px-5 flex justify-between border-4 border-double p-2 mb-2">
                          <span className="lg:w-4/12 text-center font-bold">Medio de pago</span>
                          <span className="lg:w-4/12 text-center font-bold">Banco</span>
                          <span className="lg:w-4/12 text-center font-bold">Monto</span>
                        </div>

                        {message.pagos && message.pagos.length > 0 ? (
                          message.pagos.map(pago => (
                            <div key={pago.id} className="px-5 flex justify-between border-b py-1">
                              <span className="lg:w-4/12 text-start">{pago.metodo_pago_nombre ? formatText(pago.metodo_pago_nombre) : ''}</span>
                              <span className="lg:w-4/12 text-center">{pago.banco && pago.aplica_igtf == false ? formatText(pago.banco) : 'N/A'}</span>
                              <span className="lg:w-4/12 text-right">{formatMoney(pago.monto, pago.moneda)}</span>
                            </div>
                          ))
                        ) : (
                          <div className="px-5 text-center border-b py-2">
                            No se encontraron pagos asociadas
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="w-full lg:w-3/12 px-1 pr-0 pt-1 pb-1 mt-0">
                      {(message.monto_pagado_divisas > 0.0) ?
                        (<div className="mt-3 border border-gray-200 rounded-lg shadow-sm bg-gray-50">

                          <div className="p-4 pb-2 border-b border-gray-200">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">
                              Total pagado en divisas
                            </label>
                          </div>

                          <div className="p-4 pt-0 text-right border-b border-gray-200">
                            <label className="border-0 px-3 text-blueGray-600 rounded text-lg font-bold">
                              {formatMoney(message.monto_pagado_divisas, message.moneda)}
                            </label>
                          </div>

                          <div className="p-4 pb-2 border-b border-gray-200">
                            <label className="block text-blueGray-600 text-xs font-bold mb-2">
                              {'IGTF ' + formatDecimal(message.igtf_porcentaje) + '%' }
                            </label>
                          </div>

                          <div className="p-4 pt-0 text-right">
                            <label className="border-0 px-3 text-blueGray-600 rounded text-lg font-bold">
                              {formatMoney(message.igtf_monto_aplicada != null ? message.igtf_monto_aplicada : (message.igtf_monto || 0), message.moneda)}
                            </label>
                          </div>
                        </div>)
                        :
                        (<div className="apply_igtf mt-3 rounded-lg shadow-sm bg-gray-50">
                        </div>)
                      }
                    </div>
                  </div>
                  {/* (2026-05-15) Totales en C2 (_aplicada): la factura fue
                      emitida en `message.moneda`. Fallback a C1 (VES) si la
                      factura es legacy y no tiene columnas _aplicada. */}
                  {(() => {
                    const baseC2 = message.total_base_aplicada != null ? message.total_base_aplicada : message.total_base;
                    const ivaC2 = message.total_impuestos_aplicada != null ? message.total_impuestos_aplicada : message.total_impuestos;
                    const igtfC2 = message.igtf_monto_aplicada != null ? message.igtf_monto_aplicada : (message.igtf_monto || 0);
                    const netoC2 = message.total_neto_aplicada != null ? message.total_neto_aplicada : message.total_neto;
                    const mon = message.moneda;
                    return (
                  <div className="w-full lg:w-4/12 px-0 mt-0">
                    {/* (H7, auditoría 2026-08-02) QUÉ SUMA CADA SUPERFICIE —
                        discrepancia PREEXISTENTE de agregados, documentada sin
                        cambiar cifras: esta modal y la de proforma suman las
                        FILAS HIJAS `recargos_globales` (tabla recargos_factura /
                        recargos_prefactura), mientras la vista pública imprime
                        el AGREGADO de cabecera `totales.total_recargos(_aplicada)`
                        que arma el serializador (documento_generation_routes.py:
                        :964-967) y que es también el que usa el PDF. Con datos
                        coherentes los dos valores coinciden; si divergieran, la
                        diferencia está en el backend (cabecera vs hijas), no en
                        estas vistas. */}
                    {/* (2026-08-02, ítem 32-i) RECARGOS GLOBALES del documento.
                        `recargos_globales` [{descripcion, monto, monto_aplicada}]
                        ya viajaba en el schema de detalle (schemas.py:1209) y NO
                        se pintaba: el importe estaba prorrateado dentro de la
                        base y el usuario no tenía forma de saber de dónde salía.
                        Se muestra en la moneda del documento (monto_aplicada con
                        fallback a monto/C1). Render ESTRICTAMENTE condicional:
                        un documento sin recargos se ve exactamente igual.
                        (2026-08-03, P3) La sección de DESCUENTOS globales ya SÍ
                        se construye: el backend tiene la tabla hija
                        `descuentos_factura` y el schema de detalle expone
                        `descuentos_globales` [{descripcion, monto,
                        monto_aplicada}]. Se pinta justo debajo, con el MISMO
                        render condicional. */}
                    {(message.recargos_globales || []).length > 0 && (
                      <div className="w-full mb-3 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg mb-1">Recargos</label>
                        {message.recargos_globales.map((rg, idx) => (
                          <div key={rg.id || idx} className="flex w-full text-sm justify-between">
                            <span className="text-blueGray-600 pr-2 truncate">
                              {formatText(rg.descripcion || '')}
                            </span>
                            <span className="text-blueGray-700 whitespace-nowrap">
                              {formatMoney(valorAplicado(rg, 'monto'), mon)}
                            </span>
                          </div>
                        ))}
                        <div className="flex w-full justify-end border-t border-gray-200 mt-1 pt-1">
                          <label className="text-blueGray-600 font-bold text-md">
                            Total recargos:{' '}
                            {formatMoney(
                              Math.round(
                                message.recargos_globales.reduce(
                                  (acc, rg) => acc + valorAplicado(rg, 'monto'), 0,
                                ) * 100,
                              ) / 100,
                              mon,
                            )}
                          </label>
                        </div>
                      </div>
                    )}
                    {/* (2026-08-03, P3) DESCUENTOS GLOBALES del documento —
                        espejo EXACTO del bloque de recargos de arriba, mismo
                        origen (filas hijas `descuentos_globales`, aquí
                        descuentos_factura) y misma resolución de moneda
                        (`monto_aplicada` con fallback a `monto`/C1, vía
                        `valorAplicado`). Render ESTRICTAMENTE condicional: un
                        documento sin descuentos globales —todo el histórico— se
                        ve exactamente igual que antes. */}
                    {(message.descuentos_globales || []).length > 0 && (
                      <div className="w-full mb-3 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg mb-1">Descuentos</label>
                        {message.descuentos_globales.map((dg, idx) => (
                          <div key={dg.id || idx} className="flex w-full text-sm justify-between">
                            <span className="text-blueGray-600 pr-2 truncate">
                              {formatText(dg.descripcion || '')}
                            </span>
                            <span className="text-blueGray-700 whitespace-nowrap">
                              {formatMoney(valorAplicado(dg, 'monto'), mon)}
                            </span>
                          </div>
                        ))}
                        <div className="flex w-full justify-end border-t border-gray-200 mt-1 pt-1">
                          <label className="text-blueGray-600 font-bold text-md">
                            Total descuentos:{' '}
                            {formatMoney(
                              Math.round(
                                message.descuentos_globales.reduce(
                                  (acc, dg) => acc + valorAplicado(dg, 'monto'), 0,
                                ) * 100,
                              ) / 100,
                              mon,
                            )}
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="flex w-full mb-1">
                      <div className="w-full lg:w-5/12 px-4 mt-3">
                        <label className="block text-blueGray-600 font-bold text-lg">Base imponible</label>
                      </div>
                      <div className="w-full lg:w-7/12 px-4 mt-3 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(baseC2, mon)}
                        </label>
                      </div>
                    </div>
                    <div className="flex w-full mb-1">
                      <div className="w-full lg:w-4/12 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg">I.V.A.</label>
                      </div>
                      <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(ivaC2, mon)}
                        </label>
                      </div>
                    </div>
                    <div className="flex w-full mb-1">
                      <div className="w-full lg:w-4/12 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg">IGTF</label>
                      </div>
                      <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(igtfC2, mon)}
                        </label>
                      </div>
                    </div>
                    <div className="flex w-full mb-3">
                      <div className="w-full lg:w-4/12 px-4">
                        <label className="block text-blueGray-600 font-bold text-lg">Total</label>
                      </div>
                      <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                        <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1">
                        {formatMoney(Number(netoC2 || 0) - Number(igtfC2 || 0), mon)}
                        </label>
                      </div>
                    </div>
                    {(message.monto_pagado_divisas > 0.0) ?
                    (<div className="flex w-full mb-3">
                        <div className="w-full lg:w-4/12 px-4">
                          <label className="block text-blueGray-600 font-bold text-lg">Total + IGTF</label>
                        </div>
                        <div className="w-full lg:w-8/12 px-4 text-right pr-0">
                          <label className="text-right border-0 px-3 text-blueGray-600 bg-white rounded text-lg font-bold pr-1 ">
                          {formatMoney(netoC2, mon)}
                          </label>
                        </div>
                      </div>)
                      :
                      (<div className="hidden flex w-full mb-3">
                        <div className="w-full lg:w-3/12 px-4">
                        </div>
                        <div className="w-full lg:w-9/12 px-4 text-right">
                        </div>
                      </div>)
                    }
                  </div>
                    );
                  })()}
                </div>

    </>
  );
};

export default InvoiceDetailView;